import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkle, CaretDown, CaretRight, CheckCircle, Envelope } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import CoachRow from "./approver-coach/CoachRow";
import DigestPreviewModal from "./approver-coach/DigestPreviewModal";

/**
 * Manager Approval Coach — ranks the caller's pending leave approvals by
 * "safe to approve now". Sits above the pending table on the Team
 * Approvals tab. Collapsible so the manager can hide it once they've
 * digested the list.
 */
export default function ApproverCoachCard({ refreshKey, onChanged }) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [autoBusy, setAutoBusy] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [sendMeBusy, setSendMeBusy] = useState(false);
  const [recapHtml, setRecapHtml] = useState(null);
  const [recapBusy, setRecapBusy] = useState(false);
  const [recapSendBusy, setRecapSendBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/hr/approver-coach/rank-pending");
        if (!cancelled) {
          setItems(data.items || []);
          setSummary(data.summary || null);
        }
      } catch {
        if (!cancelled) { setItems([]); setSummary(null); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [refreshKey]);

  const autoApprove = async () => {
    const eligible = items.filter((r) => r.impact_label === "LOW" && (r.days_until_start <= 7 || r.days_requested <= 3));
    if (eligible.length === 0) {
      toast.info("No LOW-impact urgent/short leaves to auto-approve right now.");
      return;
    }
    if (!window.confirm(`Auto-approve ${eligible.length} LOW-impact leave request(s) in one click?\n\nOnly rows the AI Coach rated LOW impact AND (urgent within 7 days OR short ≤3 days) will be approved. MED/HIGH rows are untouched.`)) return;
    setAutoBusy(true);
    try {
      const { data } = await api.post("/hr/approver-coach/auto-approve-low");
      toast.success(`Auto-approved ${data.applied_count} · scanned ${data.candidates_scanned}`);
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Auto-approve failed");
    } finally {
      setAutoBusy(false);
    }
  };

  const previewDigest = async () => {
    setPreviewBusy(true);
    try {
      const { data } = await api.get("/hr/approver-coach/digest-preview");
      if (!data.html) {
        toast.info(data.note || "No digest content to preview.");
      } else {
        setPreviewHtml(data.html);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Preview failed");
    } finally {
      setPreviewBusy(false);
    }
  };

  const sendPreviewToMe = async () => {
    setSendMeBusy(true);
    try {
      const { data } = await api.post("/hr/approver-coach/digest-send-me");
      if (data.sent) {
        toast.success(`Preview emailed to ${data.to}`, {
          description: data.delivered ? "Check your inbox in ~30 seconds." : "Queued in mock mode — check the email_log.",
          duration: 8000,
        });
      } else {
        toast.info(data.note || "Nothing to send.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Send failed");
    } finally {
      setSendMeBusy(false);
    }
  };

  // Weekly Recap — Sunday-evening email. Looks BACKWARD (last 7d
  // decisions) and FORWARD (next 7d coverage gaps).
  const previewRecap = async () => {
    setRecapBusy(true);
    try {
      const { data } = await api.get("/hr/approver-coach/recap-preview");
      if (!data.html) {
        toast.info(data.note || "No recap content yet.");
      } else {
        setRecapHtml(data.html);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Recap preview failed");
    } finally {
      setRecapBusy(false);
    }
  };

  const sendRecapToMe = async () => {
    setRecapSendBusy(true);
    try {
      const { data } = await api.post("/hr/approver-coach/recap-send-me");
      if (data.sent) {
        toast.success(`Weekly recap emailed to ${data.to}`, {
          description: data.delivered ? "Check your inbox in ~30 seconds." : "Queued in mock mode — check the email_log.",
          duration: 8000,
        });
      } else {
        toast.info(data.note || "Nothing to send.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Send failed");
    } finally {
      setRecapSendBusy(false);
    }
  };

  if (!loading && items.length === 0) return null;

  return (
    <div className="mx-3 md:mx-5 mb-4 border border-zinc-950" data-testid="approver-coach-card">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="approver-coach-toggle"
        className="w-full bg-zinc-950 text-white px-4 py-2.5 flex items-center justify-between hover:bg-zinc-800 transition-colors"
      >
        <div className="flex items-center gap-2">
          {open ? <CaretDown size={12} className="text-[#FF3E00]" /> : <CaretRight size={12} className="text-[#FF3E00]" />}
          <Sparkle size={14} weight="fill" className="text-[#FF3E00]" />
          <span className="font-mono text-[11px] tracking-[0.18em] uppercase">/ AI Approval Coach</span>
          {summary && (
            <span className="text-[10px] font-mono text-zinc-400 ml-2">
              {summary.low_impact} LOW · {summary.med_impact} MED · {summary.high_impact} HIGH
              {summary.urgent_count > 0 && (
                <span className="text-[#FF3E00] ml-2">· {summary.urgent_count} URGENT</span>
              )}
            </span>
          )}
        </div>
        {loading && <span className="text-[10px] font-mono text-zinc-400 pulse-dot">RANKING…</span>}
      </button>
      {open && (
        <div className="p-4 bg-zinc-50">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-zinc-600 font-mono tracking-wide">
              RANKED BY TEAM-COVERAGE IMPACT · APPROVE FROM THE TOP FOR THE SAFEST BATCH
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={previewDigest}
                disabled={previewBusy}
                data-testid="approver-coach-preview-btn"
                title="See exactly what your Monday digest email will look like."
                className="font-mono text-[10px] tracking-[0.15em] uppercase border border-zinc-950 px-3 py-1.5 hover:bg-zinc-950 hover:text-white disabled:opacity-40 flex items-center gap-1.5"
              >
                <Envelope size={11} weight="fill" />
                {previewBusy ? "LOADING…" : "PREVIEW DIGEST"}
              </button>
              <button
                onClick={previewRecap}
                disabled={recapBusy}
                data-testid="approver-coach-recap-btn"
                title="See your Sunday-evening weekly recap — last week's decisions plus next week's coverage gaps."
                className="font-mono text-[10px] tracking-[0.15em] uppercase border border-zinc-950 px-3 py-1.5 hover:bg-zinc-950 hover:text-white disabled:opacity-40 flex items-center gap-1.5"
              >
                <Envelope size={11} weight="fill" />
                {recapBusy ? "LOADING…" : "WEEKLY RECAP"}
              </button>
              {summary?.low_impact > 0 && (
                <button
                  onClick={autoApprove}
                  disabled={autoBusy}
                  data-testid="approver-coach-auto-approve-btn"
                  title="Approve every LOW-impact leave that is either urgent (<=7 days) or short (<=3 days)."
                  className="font-mono text-[10px] tracking-[0.15em] uppercase bg-emerald-600 text-white px-3 py-1.5 hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-1.5"
                >
                  <CheckCircle size={11} weight="fill" />
                  {autoBusy ? "APPROVING…" : `AUTO-APPROVE ${summary.low_impact} LOW`}
                </button>
              )}
            </div>
          </div>
          <div className="space-y-2">
            {items.map((it, idx) => (
              <CoachRow key={it.leave_id} it={it} idx={idx} />
            ))}
          </div>
        </div>
      )}

      {previewHtml && (
        <DigestPreviewModal
          html={previewHtml}
          onClose={() => setPreviewHtml(null)}
          onSendMe={sendPreviewToMe}
          sending={sendMeBusy}
        />
      )}

      {recapHtml && (
        <DigestPreviewModal
          html={recapHtml}
          onClose={() => setRecapHtml(null)}
          onSendMe={sendRecapToMe}
          sending={recapSendBusy}
          title="Your next Sunday-evening recap"
          eyebrow="SMARTERP / WEEKLY RECAP PREVIEW"
        />
      )}
    </div>
  );
}
